<template>
  <sfs-base-list-filter
    title="Filter Overtime"
    :table-name="tableName"
    :applied-filters="appliedFilters"
    :displayed-columns="displayedColumns"
    :available-columns="availableColumns"
    :column-picker="false"
    @filters-applied="onFiltersApplied"
    @filters-reset="onDefaultPresetApplied"
    @filter-cleared="onFilterCleared"
    :advanced-filter="false"
    :popover-filter="true"
  >
    <template #quick-filters>
      <b-form-group
        label="Checked Out Between"
        label-for="quick-filter-checked_out"
        label-sr-only
      >
        <div class="d-flex">
          <sfs-date-range-picker
            id="quick-filter-checked_out"
            :value="checkedOutRange"
            placeholder="Select Date Range"
            @on-change="onCheckedOutRangeChanged"
          />
          <b-input-group-append>
            <b-button
              id="quick-filter-apply-button"
              @click="applyFilters"
              variant="primary"
            >
              <i class="fa fa-search" />
            </b-button>
          </b-input-group-append>
        </div>
      </b-form-group>
    </template>

    <template #popover-filters>
      <b-row>
        <b-col cols="12">
          <b-form-group
            label="Regular Hours Start"
            label-for="advanced-filter-regular_starts_at"
            label-cols-lg="5"
            content-cols-lg="7"
          >
            <sfs-time-picker
              id="advanced-filter-regular_starts_at"
              v-model="filters.regular_starts_at"
            />
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group
            label="Regular Hours End"
            label-for="advanced-filter-regular_ends_at"
            label-cols-lg="5"
            content-cols-lg="7"
          >
            <sfs-time-picker
              id="advanced-filter-regular_ends_at"
              v-model="filters.regular_ends_at"
            />
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group
            label="Include Weekends"
            label-for="advanced-filter-include_weekends"
            label-cols-lg="5"
            content-cols-lg="7"
          >
            <b-form-checkbox
              id="advanced-filter-include_weekends"
              v-model="filters.include_weekends"
              switch
            >
              Count weekend hours as overtime
            </b-form-checkbox>
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group
            label="Clients"
            label-for="advanced-filter-client_ids"
            label-cols-lg="5"
            content-cols-lg="7"
          >
            <client-select
              id="advanced-filter-client_ids"
              placeholder="All"
              multiple
              :value="clients || []"
              @input="onClientChange"
              :persist-state="true"
            />
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group
            label="Statuses"
            label-for="advanced-filter-statuses"
            label-cols-lg="5"
            content-cols-lg="7"
          >
            <v-select
              id="advanced-filter-statuses"
              v-model="filters.statuses"
              :options="statusOptions"
              placeholder="All"
              :reduce="(status) => status.value"
              multiple
            />
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group
            :label="fieldLabels.assigned_to_id"
            label-for="advanced-filter-assigned_to_id"
            label-cols-lg="5"
            content-cols-lg="7"
          >
            <user-select
              id="advanced-filter-assigned_to_id"
              placeholder="All"
              :value="assignedTo"
              @input="onAssignedToChange"
            />
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group
            :label="fieldLabels.location_id"
            label-for="advanced-filter-location_id"
            label-cols-lg="5"
            content-cols-lg="7"
          >
            <location-select
              id="advanced-filter-location_id"
              :client-id="locationSelectClientId"
              placeholder="All"
              :value="location"
              @input="onLocationChange"
            />
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group
            :label="fieldLabels.service_id"
            label-for="advanced-filter-service_id"
            label-cols-lg="5"
            content-cols-lg="7"
          >
            <service-select
              id="advanced-filter-service_id"
              placeholder="All"
              :value="service"
              @input="onServiceChange"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </template>

    <template #right-tray>
      <sfs-download-button
        v-b-tooltip.hover
        :provider-params="filters"
        filename="Overtime Report.csv"
        :file-provider="$analyticsAPI.getWorkOrdersOvertimeReportSpreadsheet"
        title="Download spreadsheet"
      />
    </template>
  </sfs-base-list-filter>
</template>
<script>
import LocationSelect from '@/components/admin/locations/Select.vue';
import ClientSelect from '@/components/admin/clients/Select.vue';
import UserSelect from '@/components/admin/users/Select.vue';
import ServiceSelect from '@/components/admin/services/Select.vue';
import listFilterMixin from '@/mixins/listFilterMixin';
import { WORK_ORDER_FORM_LABELS } from '@/constants/workOrders';
import {
  WORK_ORDER_OVERTIME_DEFAULT_REGULAR_ENDS_AT,
  WORK_ORDER_OVERTIME_DEFAULT_REGULAR_STARTS_AT,
  WORK_ORDER_OVERTIME_REPORT_TABLE_NAME,
  WORK_ORDER_OVERTIME_REPORT_TABLE_COLUMN_OPTIONS,
  WORK_ORDER_OVERTIME_REPORT_TABLE_DEFAULT_COLUMNS,
} from '@/constants/reports';
import { DateTime } from 'luxon';

export default {
  name: 'OvertimeListFilter',
  mixins: [listFilterMixin],
  components: {
    LocationSelect,
    UserSelect,
    ClientSelect,
    ServiceSelect,
  },
  data() {
    return {
      availableColumns: WORK_ORDER_OVERTIME_REPORT_TABLE_COLUMN_OPTIONS,
      fieldLabels: WORK_ORDER_FORM_LABELS,
      tableName: WORK_ORDER_OVERTIME_REPORT_TABLE_NAME,
      statusOptions: [],
      checkedOutRange: [
        this.initialFilters.checked_out_from,
        this.initialFilters.checked_out_to,
      ],
    };
  },
  mounted() {
    this.getStatusOptions();
    this.loadInitialFilters();
  },
  methods: {
    defaultFilters() {
      return {
        regular_starts_at: WORK_ORDER_OVERTIME_DEFAULT_REGULAR_STARTS_AT,
        regular_ends_at: WORK_ORDER_OVERTIME_DEFAULT_REGULAR_ENDS_AT,
        include_weekends: true,
        checked_out_from: this.$options.filters.dateFormat(DateTime.now().minus({ month: 6 }).toISODate()),
        checked_out_to: this.$options.filters.dateFormat(DateTime.now().toISODate()),
      };
    },
    applyPresetFilter(filters, columns) {
      this.filters = filters;
      this.displayedColumns = columns;
      this.checkedOutRange = [filters.checked_out_from, filters.checked_out_to];
      this.applyFilters();
    },
    onDefaultPresetApplied() {
      this.clients = null;
      this.location = null;
      this.assignedTo = null;
      this.service = null;
      this.applyPresetFilter(this.defaultFilters(), WORK_ORDER_OVERTIME_REPORT_TABLE_DEFAULT_COLUMNS);
    },
    onLocationChange(location) {
      this.filters.location_id = location ? location.id.toString() : '';
      this.location = location;
    },
    onClientChange(selected) {
      const list = Array.isArray(selected) ? selected.filter(Boolean) : (selected ? [selected] : []);
      if (list.length) {
        this.$set(this.filters, 'client_ids', list.map((c) => String(c.id)));
        this.clients = list;
      } else {
        this.$delete(this.filters, 'client_ids');
        this.clients = null;
      }
    },
    onAssignedToChange(user) {
      this.filters.assigned_to_id = user ? user.id.toString() : '';
      this.assignedTo = user;
    },
    onServiceChange(service) {
      this.filters.service_id = service ? service.id.toString() : '';
      this.service = service;
    },
    onCheckedOutRangeChanged(dates) {
      if (dates[0]) {
        this.filters.checked_out_from = this.$options.filters.dateFormat(dates[0].toISOString());
      } else {
        this.filters.checked_out_from = '';
      }
      if (dates[1]) {
        this.filters.checked_out_to = this.$options.filters.dateFormat(dates[1].toISOString());
      } else {
        this.filters.checked_out_to = '';
      }
    },
    getStatusOptions() {
      this.$workOrdersAPI.getStatusOptions().then((response) => {
        const options = response.data.status_options.map((option) => {
          return { label: option[0], value: option[1] };
        });
        this.statusOptions = [...options];
      });
    },
    loadInitialFilters() {
      const loadLocation = new Promise((resolve) => {
        if (this.initialFilters.location_id) {
          this.$locationsAPI.get(this.initialFilters.location_id).then((location) => {
            resolve(location);
          });
        } else {
          resolve(null);
        }
      });

      const loadClients = new Promise((resolve) => {
        const ids = this.initialFilters.client_ids || [];
        if (!ids.length) {
          resolve([]);
          return;
        }
        Promise.all(ids.map((id) => this.$clientsAPI.get(id))).then((clients) => resolve(clients));
      });

      const loadService = new Promise((resolve) => {
        if (this.initialFilters.service_id) {
          this.$servicesAPI.get(this.initialFilters.service_id).then((service) => {
            resolve(service);
          });
        } else {
          resolve(null);
        }
      });

      const loadAssignedTo = new Promise((resolve) => {
        if (this.initialFilters.assigned_to_id) {
          this.$usersAPI.get(this.initialFilters.assigned_to_id).then((assignedTo) => {
            resolve(assignedTo);
          });
        } else {
          resolve(null);
        }
      });

      Promise.all([loadLocation, loadClients, loadService, loadAssignedTo]).then((values) => {
        this.location = values[0];
        this.clients = values[1] && values[1].length ? values[1] : null;
        this.service = values[2];
        this.assignedTo = values[3];
        this.applyFilters();
      });
    },
  },
  computed: {
    locationSelectClientId() {
      if (this.filters.client_ids && this.filters.client_ids.length === 1) {
        return this.filters.client_ids[0];
      }
      return null;
    },
  },
};
</script>
